/**
 * Passphrase-based symmetric sealing.
 *
 * `deriveKey` runs Argon2id over the passphrase to produce a 32-byte key.
 * `seal`/`open` use AES-256-GCM via the Web Crypto API.
 *
 * Sealed blob layout (bytes): [nonce:12][ciphertext+tag:...].
 */
import { argon2id } from '@noble/hashes/argon2.js';
import { concat, randomBytes } from './bytes.js';

/**
 * TS 5.7 tightened `Uint8Array` to `Uint8Array<ArrayBufferLike>`, which is not
 * assignable to the WebCrypto `BufferSource` contract (which requires
 * `ArrayBuffer`, not `SharedArrayBuffer`). Copy into a fresh ArrayBuffer so
 * the type checker is happy and the caller's storage can be zeroed safely.
 */
function asBuffer(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(bytes.length));
  out.set(bytes);
  return out;
}

export interface Argon2Params {
  readonly memoryKiB: number;
  readonly iterations: number;
  readonly parallelism: number;
}

/**
 * Interactive defaults (~64 MiB, 3 iters). Tune upward for higher-value
 * material once we know what devices we ship to.
 */
export const DEFAULT_ARGON2_PARAMS: Argon2Params = Object.freeze({
  memoryKiB: 64 * 1024,
  iterations: 3,
  parallelism: 1,
});

const NONCE_BYTES = 12;
const KEY_BYTES = 32;
const SALT_BYTES = 16;

export function generateSalt(): Uint8Array {
  return randomBytes(SALT_BYTES);
}

// Returns a Promise so callers can stay uniform if we later swap in a WASM
// impl that runs off the main thread — argon2id itself is synchronous today.
// eslint-disable-next-line @typescript-eslint/require-await
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  params: Argon2Params = DEFAULT_ARGON2_PARAMS,
): Promise<Uint8Array> {
  if (salt.length < 8) {
    throw new Error('salt must be at least 8 bytes');
  }
  const passphraseBytes = new TextEncoder().encode(passphrase);
  return argon2id(passphraseBytes, salt, {
    t: params.iterations,
    m: params.memoryKiB,
    p: params.parallelism,
    dkLen: KEY_BYTES,
  });
}

async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  if (rawKey.length !== KEY_BYTES) {
    throw new Error(`sealing key must be ${String(KEY_BYTES)} bytes`);
  }
  return crypto.subtle.importKey('raw', asBuffer(rawKey), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

export async function seal(
  rawKey: Uint8Array,
  plaintext: Uint8Array,
  associatedData?: Uint8Array,
): Promise<Uint8Array> {
  const key = await importAesKey(rawKey);
  const nonce = randomBytes(NONCE_BYTES);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: asBuffer(nonce),
        ...(associatedData !== undefined && { additionalData: asBuffer(associatedData) }),
      },
      key,
      asBuffer(plaintext),
    ),
  );
  return concat(nonce, ciphertext);
}

export async function open(
  rawKey: Uint8Array,
  sealed: Uint8Array,
  associatedData?: Uint8Array,
): Promise<Uint8Array> {
  if (sealed.length < NONCE_BYTES + 16) {
    throw new Error('sealed blob too short');
  }
  const key = await importAesKey(rawKey);
  const nonce = sealed.subarray(0, NONCE_BYTES);
  const ciphertext = sealed.subarray(NONCE_BYTES);
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: asBuffer(nonce),
      ...(associatedData !== undefined && { additionalData: asBuffer(associatedData) }),
    },
    key,
    asBuffer(ciphertext),
  );
  return new Uint8Array(plaintext);
}
